import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from './icons';

const MultiSelectDropdown = ({ label, options, selectedOptions, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const listboxRef = useRef(null);

  const safeLabel = label.replace(/\s+/g, '-');

  const handleToggle = () => {
      if (disabled) return;
      setIsOpen(prev => !prev);
  };

  const handleOptionClick = (option) => {
    const newSelectedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];
    onChange(newSelectedOptions);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
    if (isOpen && listboxRef.current) {
        const firstSelected = options.findIndex(opt => selectedOptions.includes(opt));
        setActiveIndex(firstSelected !== -1 ? firstSelected : 0);
        listboxRef.current.focus();
    }
    //eslint-disable-next-line
  }, [isOpen]);

  const handleButtonKeyDown = (event) => {
    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        setIsOpen(true);
    }
  };

  const handleListboxKeyDown = (event) => {
    if (options.length === 0) return;
    event.preventDefault();

    switch (event.key) {
        case 'Escape':
            setIsOpen(false);
            buttonRef.current?.focus();
            break;
        case 'ArrowDown':
            setActiveIndex(prev => (prev + 1) % options.length);
            break;
        case 'ArrowUp':
            setActiveIndex(prev => (prev - 1 + options.length) % options.length);
            break;
        case 'Enter':
        case ' ':
            handleOptionClick(options[activeIndex]);
            break;
        case 'Tab':
            setIsOpen(false);
            break;
        default:
            // No action needed for other keys
            break;
    }
  };
  
  const getButtonText = () => {
    if (selectedOptions.length === 0) return label;
    if (selectedOptions.length === 1) return selectedOptions[0];
    return `${selectedOptions.length} ${label}s Selected`;
  };

  useEffect(() => {
    if (isOpen && listboxRef.current) {
        const optionEl = listboxRef.current.querySelector(`#option-${safeLabel}-${activeIndex}`);
        optionEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, isOpen, safeLabel]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div>
        <button
          ref={buttonRef}
          type="button"
          className="inline-flex justify-between w-full min-w-[150px] rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-500"
          id={`${safeLabel}-menu-button`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={handleToggle}
          onKeyDown={handleButtonKeyDown}
          disabled={disabled}
        >
          <span className="truncate">{getButtonText()}</span>
          <ChevronDown className="-mr-1 ml-2 h-5 w-5" />
        </button>
      </div>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
          <ul
            ref={listboxRef}
            className="py-1 max-h-60 overflow-y-auto focus:outline-none"
            role="listbox"
            aria-labelledby={`${safeLabel}-menu-button`}
            aria-multiselectable="true"
            tabIndex={-1}
            onKeyDown={handleListboxKeyDown}
            aria-activedescendant={options.length > 0 ? `option-${safeLabel}-${activeIndex}` : undefined}
          >
            {options.length > 0 ? options.map((option, index) => (
              <li
                key={option}
                id={`option-${safeLabel}-${index}`}
                className={`flex items-center px-4 py-2 text-sm text-slate-700 cursor-pointer ${activeIndex === index ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
                role="option"
                aria-selected={selectedOptions.includes(option)}
                onClick={() => handleOptionClick(option)}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 pointer-events-none"
                  checked={selectedOptions.includes(option)}
                  readOnly
                  tabIndex={-1}
                />
                <span className="ml-3 block text-sm font-medium text-slate-700 cursor-pointer">
                  {option}
                </span>
              </li>
            )) : (
                <li className="px-4 py-2 text-sm text-slate-500" role="option" aria-disabled="true" aria-selected="false">No options available</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;